import * as React from 'react';
import { connect } from 'react-redux';
import * as  _ from 'lodash';
import { Controlled as ReactCodeMirror } from 'react-codemirror2';
import { IStore } from '../../../../redux/initialState';
import { useCallback, useEffect, useRef, useState } from 'react';
import ResizablePanels from '../../../components/ResizablePanels/ResizablePanels';
import { AlanButtonPopupEditorModes } from '../AlanButtonPopupsEditor/alanButtonPopupEditorModes';
import { AllDevicesIconSvg, DesktopIconSvg, MobileIconSvg, TabletIconSvg, InfoIconSvg } from './AlanButtonPopupSettingsIcons';
import { AutoFormatIconSvg } from './alanPopupEditorIcons';
import animateScroll from '../../../../services/mini/animateScroll';
import { DeviceMode } from './deviceMode';
import { getProjectOptionsByEnvironment } from '../../../../services/mini/getProjectOptionsByEnvironment';
import { IProject } from '../../../../models/IProject';
import { prepareJsonString } from '../../../../services/mini/prepareJsonString';
import { Environment } from '../../../../const/environment';
import ComboBox from '../../../components/ComboBox/ComboBox';
import ComboBoxDefaultItem from '../../../components/ComboBoxDefaultItem/ComboBoxDefaultItem';
import { ColorPicker } from '../../../components/ColorPicker/ColorPicker';
import DevicePreview from './DevicePreview';
import { DEVICE_SIZES } from './deviceSize';
import { IAlanButtonPreviewOptions } from '../../../../models/IAlanButtonPreviewOptions';
import { InfoPanel, InfoPanelIcon } from '../../../components/InfoPanel/InfoPanel';
import CODE_MIRROR_OPTIONS from '../../../../const/codeMirrorOptions';
import ReactTooltip from 'react-tooltip';
import CodeEditorWithSearch from './CodeEditorWithSearch';
import { CSS_POPUP_EDITOR, HTML_POPUP_EDITOR, PARAMS_POPUP_EDITOR } from '../../../../const/editorNames';
import { cleanupParams } from '../../../../services/mini/cleanupParams';
const CodeMirror = require('codemirror/lib/codemirror');

const htmlPretty = require('pretty');
const cssBeautify = require('cssbeautify');
const Handlebars = require('handlebars/dist/cjs/handlebars');

export enum PanelWidthStates {
    Normal = 'Normal',
    WithNoLabels = 'WithNoLabels',
    WithNoIcons = 'WithNoIcons',
    Minimized = 'Minimized',
}

const PLATFORMS = [
    { key: 'web', title: 'Web' },
    { key: 'ios', title: 'iOS' },
    { key: 'android', title: 'Android' },
];

const SCALES = [
    { key: '50', title: '50%' },
    { key: '75', title: '75%' },
    { key: '100', title: '100%' },
    { key: '125', title: '125%' },
    { key: '150', title: '150%' },
];


type AlanButtonPopupSettingsFormProps = {
    project: IProject
    environment: Environment
    html: string
    style: string
    params: string
    type: string
    paramsCanBeCompiledInPreview: boolean
    previewMode: AlanButtonPopupEditorModes
    deviceMode: DeviceMode
    buttonMarginInPopup?: number
    setDeviceMode: (DeviceMode) => void;
    onDataUpdated: (propName: string, value: string) => void;
    onSave: () => void;
    openFullScreenMode: () => void;
    closeFullScreenMode: () => void;
}

function AlanButtonPopupSettingsForm({
    project, environment, html, style, params, type, paramsCanBeCompiledInPreview, previewMode, deviceMode, buttonMarginInPopup,
    setDeviceMode,
    onDataUpdated,
    onSave,
    openFullScreenMode,
    closeFullScreenMode
}: AlanButtonPopupSettingsFormProps) {

    const isStage = environment === Environment.Staging;

    const htmlCmOptions = {
        lineNumbers: true,
        mode: "htmlmixed",
        readOnly: isStage ? false : true,
        showCursorWhenSelecting: true,
        searchKey: HTML_POPUP_EDITOR
    };
    const cssCmOptions = {
        lineNumbers: true,
        mode: "css",
        readOnly: isStage ? false : true,
        showCursorWhenSelecting: true,
        searchKey: CSS_POPUP_EDITOR
    };
    const paramsCmOptions = {
        lineNumbers: true,
        readOnly: isStage ? false : true,
        mode: "application/json",
        showCursorWhenSelecting: true,
        searchKey: PARAMS_POPUP_EDITOR
    };

    const [popupHtml, setPopupHtml] = useState(html || '');
    const [popupCss, setPopupCss] = useState(style || '');
    const [popupParams, setPopupParams] = useState(params ? (cleanupParams(params) === '' ? '{}' : params) : '{}');
    const [htmlPanelState, setHtmlPanelState] = useState(PanelWidthStates.Normal);
    const [cssPanelState, setCssPanelState] = useState(PanelWidthStates.Normal);
    const [paramsPanelState, setParamsPanelState] = useState(PanelWidthStates.Normal);

    const [scale, setScale] = useState('100');
    const [deviceBgColor, setDeviceBgColor] = useState('#ffffff');
    const [platform, setPlatform] = useState('web');
    const [frameSize, setFrameSize] = useState(DEVICE_SIZES[deviceMode]);
    const [currentEditor, setCurrentEditor] = useState(null);
    const previewBodyRef = useRef();

    const htmlEditorRef = React.createRef<typeof CodeMirror>();
    const cssEditorRef = React.createRef<typeof CodeMirror>();
    const paramsEditorRef = React.createRef<typeof CodeMirror>();

    const [width, height] = frameSize;

    const scrollViewCb = useCallback(() => {
        setTimeout(() => {
            animateScroll(previewBodyRef.current, 0);
            animateScroll(previewBodyRef.current, 0, true);
        }, 10);
    }, []);

    useEffect(() => {
        scrollViewCb();
    }, [scrollViewCb]);

    useEffect(() => {
        scrollViewCb();
    }, [deviceMode, scrollViewCb]);

    const prettifyHtmlCb = useCallback(() => {
        const newHtml = htmlPretty(html);
        setPopupHtml(newHtml);
        onDataUpdated('html', newHtml);
        htmlEditorRef.current.editor.setValue(newHtml);
    }, [html, htmlEditorRef, onDataUpdated]);

    const prettifyStyleCb = useCallback(() => {
        const newStyle = cssBeautify(style);
        setPopupCss(newStyle);
        onDataUpdated('style', newStyle);
        cssEditorRef.current.editor.setValue(newStyle);
    }, [cssEditorRef, onDataUpdated, style]);


    const prettifyParamsCb = useCallback(() => {
        try {
            const newParams = JSON.stringify(JSON.parse(prepareJsonString(params)), null, CODE_MIRROR_OPTIONS.JSON_SPACE);
            setPopupParams(newParams);
            onDataUpdated('params', newParams);
            paramsEditorRef.current.editor.setValue(newParams);
        } catch (e) {
            console.info('Unable to format Params');
        }
    }, [onDataUpdated, params, paramsEditorRef]);

    const setDeviceModeCb = useCallback((mode: DeviceMode) => {
        setDeviceMode(mode);
        setFrameSize(DEVICE_SIZES[mode]);
    }, [setDeviceMode]);

    const changePanelState = useCallback((width, curState, cb) => {
        const normalStep = 200;
        const woLabelsStep = 130;
        const woIconsStep = 70;
        const minimizedStep = 65;

        if (width >= normalStep) {
            if (curState !== PanelWidthStates.Normal) {
                cb(PanelWidthStates.Normal);
            }
        } else if (width < normalStep && width >= woLabelsStep) {
            if (curState !== PanelWidthStates.WithNoLabels) {
                cb(PanelWidthStates.WithNoLabels);
            }
        } else if (width < woLabelsStep && width >= woIconsStep) {
            if (curState !== PanelWidthStates.WithNoIcons) {
                cb(PanelWidthStates.WithNoIcons);
            }
        } else if (width < minimizedStep) {
            if (curState !== PanelWidthStates.Minimized) {
                cb(PanelWidthStates.Minimized);
            }
        }
    }, []);

    const changeHtmlPanelSizeCb = useCallback(({ width }) => {
        changePanelState(width, htmlPanelState, setHtmlPanelState);
    }, [changePanelState, htmlPanelState]);

    const changeCssPanelSizeCb = useCallback(({ width }) => {
        changePanelState(width, cssPanelState, setCssPanelState);
    }, [changePanelState, cssPanelState]);

    const changeParamsPanelSizeCb = useCallback(({ width }) => {
        changePanelState(width, paramsPanelState, setParamsPanelState);
    }, [changePanelState, paramsPanelState]);

    let compiledHtml = '';
    let popupCompiledWithErrors = false;

    try {
        compiledHtml = Handlebars.compile(popupHtml)(popupParams ? JSON.parse(prepareJsonString(popupParams)) : {});
    } catch (e) {
        console.info('err');
        compiledHtml = popupHtml;
        popupCompiledWithErrors = true;
    }

    const projectBtnOptions = getProjectOptionsByEnvironment(project, platform, environment, [], null);

    const buttonSize = projectBtnOptions[platform]?.buttonSize || 64;
    const btnBottomPos = 40;
    const btnRightPos = 20;
    const popup2BtnMargin = 12;
    const logoUrl = projectBtnOptions[platform]?.logoIdle || 'https://studio.alan-stage.app/web/src/assets/images/embed/mic_logo.png';
    const [bgColor1, bgColor2] = projectBtnOptions[platform]?.btnOptions?.idle?.background?.color || ['rgb(34, 203, 255)', 'rgb(25, 149, 255)'];

    const rightPopupPos = buttonMarginInPopup ? (btnRightPos - buttonMarginInPopup) : btnRightPos;
    const bottomPopupPos = buttonMarginInPopup ? (btnBottomPos - buttonMarginInPopup) : (btnBottomPos + buttonSize + popup2BtnMargin);

    const btnOptions = {
        rightPopupPos,
        bottomPopupPos,
        btnBottomPos,
        bgColor1,
        bgColor2,
        buttonSize,
        logoUrl
    } as IAlanButtonPreviewOptions;

    const allDeviceBlockHeight = DEVICE_SIZES[DeviceMode.Tablet][1];
    const allDeviceBlockWidth = DEVICE_SIZES[DeviceMode.Tablet][0] + DEVICE_SIZES[DeviceMode.Mobile][0] + DEVICE_SIZES[DeviceMode.Desktop][0];

    const [scalePrevieWidth, scalePrevieHeight] = deviceMode === DeviceMode.All ? [allDeviceBlockWidth, allDeviceBlockHeight] : DEVICE_SIZES[deviceMode];
    const deviceOuterBorder = 120;
    const scalablePreviewStyle = {
        minWidth: `${(scalePrevieWidth + deviceOuterBorder) * (+scale / 100)}px`,
        maxWidth: `${(scalePrevieWidth + deviceOuterBorder) * (+scale / 100)}px`,
        width: `${(scalePrevieWidth + deviceOuterBorder) * (+scale / 100)}px`,
        height: `${(scalePrevieHeight + deviceOuterBorder) * (+scale / 100)}px`,
    };

    return <div className="alan-button-popup-settings-form">
        <ResizablePanels
            bkcolor="white"
            displayDirection="column"
            width="100%"
            height="100%"
            panelsSize={previewMode === AlanButtonPopupEditorModes.Preview ? [42, 58] : [10, 90]}
            sizeUnitMeasure="%"
            resizerColor="#bbcfe7"
            resizerSize="10px">
            <div className="alan-button-popup-settings-form__editor">
                <div className="alan-button-popup-settings-form__editor-body">
                    <div className="alan-button-popup-settings-form__fake-left-panel-border" />
                    <div className="alan-button-popup-settings-form__editor-panels" >
                        <ResizablePanels
                            bkcolor="white"
                            displayDirection="row"
                            width="100%"
                            height="100%"
                            panelsSize={[33, 33, 34]}
                            onChangePanelSizeCb={[changeHtmlPanelSizeCb, changeCssPanelSizeCb, changeParamsPanelSizeCb]}
                            sizeUnitMeasure="%"
                            resizerColor="#bbcfe7"
                            resizerBorderRightColor="#e4e6ea"
                            resizerBorderBottomColor="#e4e6ea"
                            resizerSize="20px">
                            <div className="alan-button-popup-settings-form__editor-col" >
                                <div className={`alan-button-popup-settings-form__editor-col-header ${htmlPanelState}`}>
                                    <div>
                                        <div className={`alan-button-popup-settings-form__editor-col-header-title ${htmlPanelState}`}>
                                            HTML
                                            <div
                                                className="handlebars-can-be-used-tooltip-icon"
                                                data-multiline="true"
                                                data-effect="solid"
                                                data-place="bottom"
                                                data-class="tutor-no-arrow-tooltip handlebars-can-be-used-tooltip"
                                                data-for="handlebars-can-be-used-tooltip"
                                                data-tip="You can use the Handlebars templating language to fill your HTML with values.">
                                                <InfoIconSvg
                                                    borderColor="#90C2F7"
                                                    fontColor="#90C2F7"
                                                    backgroundColor="#DDF2FF"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {isStage && <span className="pretify-section" onClick={prettifyHtmlCb}> <AutoFormatIconSvg /> <span>Format HTML</span></span>}
                                </div>
                                <div className={`alan-button-popup-settings-form__editor-col-body with-gutter`}>
                                    <CodeEditorWithSearch
                                        ref={htmlEditorRef}
                                        value={popupHtml}
                                        options={htmlCmOptions}
                                        searchKey={currentEditor}
                                        editorId={HTML_POPUP_EDITOR}
                                        onFocus={() => setCurrentEditor(HTML_POPUP_EDITOR)}
                                        onBeforeChange={(editor, data, value) => {
                                            setPopupHtml(value);
                                            onDataUpdated('html', value);
                                        }}
                                        onSave={onSave} />
                                    <ReactTooltip id="handlebars-can-be-used-tooltip" />
                                </div>

                            </div>
                            <div className="alan-button-popup-settings-form__editor-col">
                                <div className={`alan-button-popup-settings-form__editor-col-header ${cssPanelState}`}>
                                    <div>
                                        <div className={`alan-button-popup-settings-form__editor-col-header-title ${cssPanelState}`}>
                                            CSS
                                        </div>
                                    </div>

                                    {isStage && <span className="pretify-section" onClick={prettifyStyleCb}> <AutoFormatIconSvg /> <span>Format CSS</span></span>}
                                </div>
                                <div className={`alan-button-popup-settings-form__editor-col-body with-gutter`}>
                                    <CodeEditorWithSearch
                                        ref={cssEditorRef}
                                        value={popupCss}
                                        options={cssCmOptions}
                                        searchKey={currentEditor}
                                        editorId={CSS_POPUP_EDITOR}
                                        onFocus={() => setCurrentEditor(CSS_POPUP_EDITOR)}
                                        onBeforeChange={(editor, data, value) => {
                                            setPopupCss(value);
                                            onDataUpdated('style', value);
                                        }}
                                        onSave={onSave} />
                                </div>
                            </div>
                            <div className="alan-button-popup-settings-form__editor-col">
                                <div className={`alan-button-popup-settings-form__editor-col-header ${paramsPanelState}`}>
                                    <div>
                                        <div className={`alan-button-popup-settings-form__editor-col-header-title ${paramsPanelState}`}>
                                            Params
                                            {!paramsCanBeCompiledInPreview &&
                                                <div
                                                    className="params-cannot-be-compiled-icon"
                                                    data-multiline="true"
                                                    data-effect="solid"
                                                    data-place="bottom"
                                                    data-class="tutor-no-arrow-tooltip params-cannot-be-compiled-tooltip"
                                                    data-for="params-cannot-be-compiled-tooltip"
                                                    data-tip="The params object should only contain numbers, strings, boolean values or arrays/objects constructed with these values. If your object contains other types of values, we will not be able to calculate the params for compiling the template. You can edit the params object here to see how the popup will look like, but your changes won't be saved.">
                                                    <InfoIconSvg
                                                        borderColor="#EDC73F"
                                                        fontColor="#AB880D"
                                                        backgroundColor="#EDC73F"
                                                    />
                                                </div>
                                            }
                                        </div>
                                    </div>
                                    {isStage && <span className="pretify-section" onClick={prettifyParamsCb}> <AutoFormatIconSvg /> <span>Format Params</span></span>}
                                </div>
                                <div className={`alan-button-popup-settings-form__editor-col-body with-gutter`}>
                                    <CodeEditorWithSearch
                                        ref={paramsEditorRef}
                                        value={popupParams}
                                        options={paramsCmOptions}
                                        searchKey={currentEditor}
                                        editorId={PARAMS_POPUP_EDITOR}
                                        onFocus={() => setCurrentEditor(PARAMS_POPUP_EDITOR)}
                                        onBeforeChange={(editor, data, value) => {
                                            setPopupParams(value);
                                            onDataUpdated('params', value);
                                        }}
                                        onSave={onSave} />
                                    <ReactTooltip id="params-cannot-be-compiled-tooltip" />
                                </div>
                            </div>
                        </ResizablePanels>
                    </div>
                </div>
            </div>
            <div className="alan-button-popup-settings-form__preview">
                <div className="alan-button-popup-settings-form__preview-header">
                    <div className="alan-button-popup-settings-form__preview-header-title">
                        {deviceMode !== DeviceMode.All ? `${deviceMode}: ${width} x ${height}px` : ''}
                    </div>

                    <div className="alan-button-popup-settings-form__preview-header-main-controls">
                        <ComboBox
                            selectedValueId={platform}
                            values={PLATFORMS}
                            valueKey="key"
                            valueName="title"
                            width="90px"
                            renderItem={(item) => {
                                return <ComboBoxDefaultItem>
                                    {item.title}
                                </ComboBoxDefaultItem>;
                            }}
                            onValueSelect={(item) => { setPlatform(item.key); }}
                        />
                        <div className="alan-button-popup-settings-form__preview-header-device-btns">
                            <div onClick={() => setDeviceModeCb(DeviceMode.Desktop)}
                                className={`alan-button-popup-settings-form__preview-header-device-btn ${deviceMode === DeviceMode.Desktop && 'selected'}`}>
                                <DesktopIconSvg />
                            </div>
                            <div onClick={() => setDeviceModeCb(DeviceMode.Tablet)}
                                className={`alan-button-popup-settings-form__preview-header-device-btn ${deviceMode === DeviceMode.Tablet && 'selected'}`}>
                                <TabletIconSvg />
                            </div>
                            <div onClick={() => setDeviceModeCb(DeviceMode.Mobile)}
                                className={`alan-button-popup-settings-form__preview-header-device-btn ${deviceMode === DeviceMode.Mobile && 'selected'}`}>
                                <MobileIconSvg />
                            </div>
                            <div onClick={() => setDeviceModeCb(DeviceMode.All)}
                                className={`alan-button-popup-settings-form__preview-header-device-btn alan-button-popup-settings-form__preview-header-all-devices-btn  ${deviceMode === DeviceMode.All && 'selected'}`}>
                                <AllDevicesIconSvg />
                            </div>
                        </div>
                        <div className="alan-button-popup-settings-form__preview-header-device-bg-controls">
                            Device background&nbsp;&nbsp;
                            <ColorPicker
                                color={deviceBgColor}
                                onClose={() => { }}
                                width='100px'
                                height='32px'
                                isHex={true}
                                disableAlpha={true}
                                onChange={(color) => {
                                    setDeviceBgColor(color);
                                }} />
                        </div>
                        <ComboBox
                            classes="alan-button-popup-settings-form__scale-cb"
                            selectedValueId={scale}
                            values={SCALES}
                            valueKey="key"
                            valueName="title"
                            width="70px"
                            height="180px"
                            renderItem={(item) => {
                                return <ComboBoxDefaultItem>
                                    {item.title}
                                </ComboBoxDefaultItem>;
                            }}
                            onValueSelect={(item) => { setScale(item.key); scrollViewCb(); }}
                        />

                    </div>
                    <div className="alan-button-popup-settings-form__preview-header-fullscreen-btn"
                        onClick={previewMode === AlanButtonPopupEditorModes.Preview ? openFullScreenMode : closeFullScreenMode}>
                        {previewMode === AlanButtonPopupEditorModes.Preview ?
                            <i className="icon icon-alan-halfscreen-arrows" /> :
                            <i className="icon icon-alan-halfscreen-back-arrows" />
                        }
                    </div>
                </div>
                <InfoPanel title="The popup was compiled with errors. The popup you see may be outdated." visible={popupCompiledWithErrors} error={true} icon={InfoPanelIcon.Error} />
                <div className={`alan-button-popup-settings-form__preview-body `} ref={previewBodyRef}>
                    <div className={`alan-button-popup-settings-form__preview-body-with-all-devices scale-${scale}`} style={scalablePreviewStyle}>
                        {deviceMode === DeviceMode.All ?
                            <React.Fragment>
                                <DevicePreview
                                    deviceMode={DeviceMode.Desktop}
                                    deviceBgColor={deviceBgColor}
                                    btnOptions={btnOptions}
                                    css={popupCss}
                                    html={compiledHtml}
                                    type={type}
                                />
                                <DevicePreview
                                    deviceMode={DeviceMode.Tablet}
                                    deviceBgColor={deviceBgColor}
                                    btnOptions={btnOptions}
                                    css={popupCss}
                                    html={compiledHtml}
                                    type={type}
                                />
                                <DevicePreview
                                    deviceMode={DeviceMode.Mobile}
                                    deviceBgColor={deviceBgColor}
                                    btnOptions={btnOptions}
                                    css={popupCss}
                                    html={compiledHtml}
                                    type={type}
                                />
                            </React.Fragment>
                            :
                            <DevicePreview
                                deviceMode={deviceMode}
                                deviceBgColor={deviceBgColor}
                                btnOptions={btnOptions}
                                css={popupCss}
                                html={compiledHtml}
                                type={type}
                            />
                        }
                    </div>
                </div>
            </div>
        </ResizablePanels>
    </div>;
}

function mapStateToProps({ projectsInfo, user }: IStore) {
    return {
    };
}

function mapDispatchToProps(dispatch) {
    return {
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(AlanButtonPopupSettingsForm);